import { Box } from "@familyzone/component-library";
import iconBack from "assets/IconBack.svg?url";
import iconChatMini from "assets/IconChatMini.svg?url";
import iconExitModal from "assets/IconExitModal.svg?url";
import iconInfo from "assets/IconInfo.svg?url";
import iconInfoFooter from "assets/IconInfofooter.svg?url";
import clsx from "clsx";
import { getClassroomDetails } from "js/api/classApi";
import ChatEnableModal from "js/components/ChatEnableModal";
import Avatar from "js/components/shared/Avatar";
import ToggleSwitch from "js/components/shared/ToggleSwitch";
import useStore from "stores/index";
import {
	getChatOverview,
	getMessagesForClassroomUsers,
	getSelectedClassId,
	getSelectedStudentName,
	getSelectedThreadKey,
} from "stores/chatStore";
import { classroomActiveInfoV2 } from "js/utils/classroomSessionUtils";
import { userDisplayText } from "js/utils/userUtils";
import PropTypes from "prop-types";
import Tooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap.css";
import React, { Fragment } from "react";
import shallow from "zustand/shallow";
import MessagePane from "../MessagePane";
import NoEntries from "../NoEntries";
import RoomsList from "../RoomsList";
import StudentsList from "../StudentsList";
import { useUserSessionStore } from "stores/v2/userSessionStore";

class ChatWindow extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			chatOverview: [],
			selectedClassroom: {},
			selectedStudentName: getSelectedStudentName(useStore.getState()),
			selectedThreadKey: getSelectedThreadKey(useStore.getState()),
			zoom: {},
			minimized: useStore.getState().chat.chatWindowMinimized,
			hasActiveClass: false,
			showChatEnableModal: false,
			disableChatEnableModal: false,
			featureToggles: useStore.getState().featureToggles,
		};
		this.unsubUnreadMessagesCount = () => {};
		this.unsubZooms = () => {};
		this.unsubChatClassId = () => {};
		this.unSubClassStudentName = () => {};
		this.unSubSelectedThreadKey = () => {};
		this.unSubMinimizedChat = () => {};

		this.avatarColors = useStore.getState().chat.avatarColors;
	}

	componentDidMount() {
		const classroomId = this.props.classroom.sourceId || getSelectedClassId(useStore.getState());
		const selectedClassroom = useStore.getState().classroom.getClassroomV2(classroomId);

		this.unsubZooms = useStore.subscribe(
			(state) => [state.zoom.isFetching, state.zoom.data, state.zoom.chat_blocked],
			([isFetching, zoom]) => {
				this.setState({ zoom: zoom });
			},
			shallow
		);

		let hasActiveClass = false;
		if (selectedClassroom?.name) {
			const name = selectedClassroom.sourceId;
			const policyID = selectedClassroom.stableIdV1;
			useStore.getState().zoom.fetch(name, policyID);
			hasActiveClass = classroomActiveInfoV2(selectedClassroom)[0];
		}

		this.setState({
			chatOverview: this.formatChatOverview(getChatOverview(useStore.getState().chat.unreadMessagesCount)),
			selectedClassroom,
			hasActiveClass: hasActiveClass,
			disableChatEnableModal: useUserSessionStore.getState().getNeverShowChatEnable(),
		});

		if (classroomId) {
			this.handleSelectedClassroomInner(classroomId);
		}

		this.unsubUnreadMessagesCount = useStore.subscribe(
			(state) => [state.chat.unreadMessagesCount],
			(newState) => {
				this.setState({
					chatOverview: this.formatChatOverview(getChatOverview(newState)),
				});
			},
			shallow
		);

		this.unsubChatClassId = useStore.subscribe(
			(state) => state.chat.selectedClassId,

			(newClassId) => {
				const selectedClassroom = useStore.getState().classroom.getClassroomV2(newClassId);
				this.setState({ selectedClassroom });
			},
			shallow
		);

		this.unSubClassStudentName = useStore.subscribe(
			(state) => state.chat.selectedStudentName,

			(newStudentName) => this.setState({ selectedStudentName: newStudentName }),
			shallow
		);

		this.unSubSelectedThreadKey = useStore.subscribe(
			(state) => state.chat.selectedThreadKey,

			(newThreadkey) => this.setState({ selectedThreadKey: newThreadkey }),
			shallow
		);

		this.unSubMinimizedChat = useStore.subscribe(
			(state) => state.chat.chatWindowMinimized,

			(newStatus) => this.setState({ minimized: newStatus }),
			shallow
		);
	}

	componentWillUnmount() {
		this.unsubChatClassId();
		this.unSubClassStudentName();
		this.unSubSelectedThreadKey();
		this.unSubMinimizedChat();
		this.unsubZooms();
	}

	formatChatOverview = (chatOverview) => {
		if (!chatOverview) {
			return null;
		}
		const fullClassChatList = useStore
			.getState()
			.classroom.getClassrooms()
			.filter((classroom) => !(classroom.is_monitoring_class || classroom?.session?.isMonitoring))
			.map((classroom) => {
				if (!useStore.getState().chat.unreadMessagesCount.data) {
					return {
						// Note: chatOverview takes the classroom.sourceId as the key, therefore no need to switch to getClassroomIdForChat(classroom)
						classroomId: classroom.sourceId,
						hasUnreadMessages: false,
						lastUpdated: null,
					};
				}

				const unreadMessage = Object.values(useStore.getState().chat.unreadMessagesCount.data).filter(
					(overview) => overview.classroomId === classroom.sourceId
				);
				let lastUpdated = null;
				let hasUnreadMessages = false;

				if (unreadMessage.length > 0) {
					lastUpdated = unreadMessage[0].lastUpdated;
					hasUnreadMessages = unreadMessage[0].hasUnreadMessages;
				}

				return {
					classroomId: classroom.sourceId,
					hasUnreadMessages,
					lastUpdated,
				};
			});

		fullClassChatList.forEach((classChatOverview) => {
			const classroom = useStore.getState().classroom.getClassroomV2(classChatOverview.classroomId);
			const [isActiveClass] = classroomActiveInfoV2(classroom);
			classChatOverview.isActiveClass = isActiveClass;
			classChatOverview.students = classroom.students;
			classChatOverview.studentCount = classroom.memberCount;
		});

		fullClassChatList.sort((a, b) => (a.hasUnreadMessages ? -1 : 0));
		fullClassChatList.sort((a, b) => b.isActiveClass - a.isActiveClass);
		fullClassChatList.sort((a, b) => {
			if (a.isActiveClass && a.hasUnreadMessages) {
				if (a.lastUpdated > b.lastUpdated) {
					return -1;
				}
				if (a.lastUpdated < b.lastUpdated) {
					return 1;
				}
				return 0;
			}
			return 1;
		});

		return fullClassChatList;
	};

	getChatBlockedText = (isActive, chatBlocked) => {
		let chatBlockedText;

		if (!isActive) {
			chatBlockedText = <>Chat can only be turned on/off in an active session</>;
		} else {
			if (chatBlocked) {
				chatBlockedText = (
					<>
						Turn on Chat for <br /> this session
					</>
				);
			} else {
				chatBlockedText = (
					<>
						Turn off Chat for <br /> this session
					</>
				);
			}
		}

		return chatBlockedText;
	};

	handleSelectedClassroom = (event) => {
		const selectedClassroomName = event.currentTarget.attributes["data-classid"].value;
		this.handleSelectedClassroomInner(selectedClassroomName);
	};

	handleSelectedClassroomInner = async (selectedClassroomName) => {
		useStore.getState().chat.setSelectedClassId(selectedClassroomName);

		const _class = useStore.getState().classroom.getClassroomV2(selectedClassroomName);
		const classDetails = await getClassroomDetails(
			_class.id,
			true,
			true,
			true,
			false,
			_class.type,
			selectedClassroomName,
			useUserSessionStore.getState().getTeacherId()
		);
		useStore.getState().classroom.updateClassroomDataV2(classDetails);
		this.setState({ selectedClassroom: classDetails.classroom }, async () => {
			const { selectedClassroom } = this.state;
			if (selectedClassroom && this.state.zoom) {
				const hasActiveClass = classroomActiveInfoV2(selectedClassroom)[0];
				this.setState({
					hasActiveClass,
				});
			}

			if (selectedClassroom) {
				await useStore.getState().chat.fetchThreadTeachers(selectedClassroom);
				this.setState({ classroomStudents: selectedClassroom.students }, () =>
					getMessagesForClassroomUsers(useStore.getState(), selectedClassroom.students, selectedClassroom)
				);
			}
		});
	};

	handleSelectedStudent = (studentId, threadKey) => {
		useStore.getState().chat.setSelectedThreadKey(threadKey);
		useStore.getState().chat.setSelectedStudentName(studentId);
	};

	handleBackButton = () => {
		useStore.getState().chat.setSelectedClassId("");
	};

	handleBackClasslistButton = () => {
		useStore.getState().chat.setSelectedThreadKey("");
		useStore.getState().chat.setSelectedStudentName("");
	};

	renderStudentsListHeader() {
		const { selectedClassroom } = this.state;
		const _classroom = useStore.getState().classroom.getClassroomV2(selectedClassroom.id);
		const displayName = _classroom?.personalisation?.displayName || selectedClassroom.label || selectedClassroom.name;
		return (
			<div className="ClassroomChat_studentListHeader">
				<button className="ClassroomChatroom_backButton" onClick={this.handleBackButton} type="button">
					<img src={iconBack} alt="Go back to classroom list" />
				</button>
				<div className="ClassroomChat_title ClassroomChat_name">{displayName}</div>
			</div>
		);
	}

	handleToggleSwitch = async () => {
		if (this.state.hasActiveClass) {
			if (this.state.zoom.chat_blocked && !this.state.disableChatEnableModal) {
				await useStore.getState().chat.openChatEnableModal(true);
				this.setState({ showChatEnableModal: true });
				return;
			}
			this.handleToggleChatRequest();
		}
	};

	handleChatEnableSubmit = () => {
		if (this.state.disableChatEnableModal) {
			useUserSessionStore.getState().neverShowChatEnableModal();
		}
		this.handleToggleChatRequest();
		useStore.getState().chat.openChatEnableModal(false);
		this.setState({ showChatEnableModal: false });
	};

	handleEnableModalCheckbox = (isChecked) => {
		this.setState({ disableChatEnableModal: isChecked });
	};

	handleToggleChatRequest = async () => {
		const selectedClass = this.state.selectedClassroom;
		if (selectedClass) {
			useStore.getState().zoom.toggleChatBlocked();
			this.forceUpdate();
		}
	};

	renderClassroomListHeader() {
		return (
			<div className="ClassroomChat_classesHeader">
				<div className="ClassroomChat_title">Classwize Chat</div>
			</div>
		);
	}

	renderChatHeader() {
		const { selectedStudentName } = this.state;

		let fullName = "";
		const usersList = useStore.getState().users.data;

		if (usersList) {
			const user = usersList[selectedStudentName];
			if (user) {
				fullName = userDisplayText(user);
			}
		}

		return (
			<div className="ClassroomChat_studentListHeader">
				{!this.props.isEmbedded && (
					<button className="ClassroomChatroom_backButton" onClick={this.handleBackClasslistButton} type="button">
						<img src={iconBack} alt="Go back to Student list" />
					</button>
				)}
				<div className="ClassroomChat_titleAvatar">
					<Avatar color={this.avatarColors[0]} label={selectedStudentName[0]} overrideAvatarClass="ClassroomChat_titleAvatarIcon" />
				</div>
				<div>
					<div className="ClassroomChat_title ClassroomChat_studentName">{fullName}</div>
					<div className="ClassroomChat_title ClassroomChat_studentName  ClassroomChat_studentEmail">{selectedStudentName}</div>
				</div>
			</div>
		);
	}

	renderClassroomList() {
		const { chatOverview } = this.state;

		if (!chatOverview.length) {
			return (
				<NoEntries title="No classes found">
					<p>Monitoring classes will not appear here</p>
					<p>If you can’t see your classes please contact your local IT support team.</p>
				</NoEntries>
			);
		}

		return (
			<>
				<Box mb={"50px"}>
					{chatOverview.map((classChatOverview, i) => (
						<Box key={classChatOverview.classroomId}>
							<RoomsList
								classroomId={classChatOverview.classroomId}
								isActiveClass={classChatOverview.isActiveClass}
								hasUnreadMessages={classChatOverview.hasUnreadMessages}
								onClick={this.handleSelectedClassroom}
								studentCount={classChatOverview.studentCount}
							/>
							<div className="ClassroomChat_separator" />
						</Box>
					))}
				</Box>

				<div className="ClassroomChat_footer">
					<div className="ClassroomChat_disablePopup">
						<a href="https://docs.classwize.net/help/how-to-stop-classwize-chat-popup-notifications" target="_blank" rel="noreferrer">
							<img src={iconInfoFooter} alt="" /> Disable pop-up notification on your device
						</a>
					</div>
				</div>
			</>
		);
	}

	renderRightControl() {
		const { selectedStudentName, selectedClassroom, zoom, hasActiveClass } = this.state;

		if (!selectedStudentName) {
			return (
				<div className="ClassroomChatInfo_Wrapper">
					{selectedClassroom ? (
						<ToggleSwitch
							checked={!zoom.chat_blocked}
							onToggle={this.handleToggleSwitch}
							toolTipOverlayText={this.getChatBlockedText(hasActiveClass, zoom.chat_blocked)}
						/>
					) : (
						<a
							className="ClassroomChatInfo_link"
							href="https://docs.classwize.net/help/classwize-chat"
							target="_blank"
							rel="noopener noreferrer"
						>
							<Tooltip
								placement="bottom"
								trigger={["hover"]}
								destroyTooltipOnHide={true}
								overlayClassName="Tooltip"
								overlay={<span>Learn more</span>}
							>
								<img className="ClassroomChatInfo_icon" src={iconInfo} alt="" />
							</Tooltip>
						</a>
					)}
					&nbsp; {selectedClassroom && `${zoom.chat_blocked ? "  Chat off" : "  Chat on"}`}
				</div>
			);
		}

		return (
			<button className="ClassroomChat_minimizeButton" onClick={this.handleChatMinimize}>
				<img src={iconExitModal} alt="Minimize chat" />
			</button>
		);
	}

	renderHeader() {
		const { selectedStudentName, selectedClassroom } = this.state;

		if (!selectedStudentName) {
			return selectedClassroom ? this.renderStudentsListHeader() : this.renderClassroomListHeader();
		}

		return this.renderChatHeader();
	}

	renderContents() {
		const { selectedClassroom, selectedStudentName, selectedThreadKey, zoom } = this.state;

		if (!this.props.isChatWorking) {
			return (
				<NoEntries title="Chat is unavailable">
					An error has occured with Classwize Chat, if this error persists, please contact your administrator.
				</NoEntries>
			);
		}

		// Note: chatOverview takes the classroom.sourceId as the key, therefore no need to switch to getClassroomIdForChat(classroom)
		const sourceId = selectedClassroom.sourceId;
		if (!selectedStudentName) {
			return (
				<div className={`${!!selectedClassroom && !!selectedClassroom.name ? "ClassroomChat_content" : "ClassroomChat_contentRooms"}`}>
					{selectedClassroom && sourceId ? (
						<StudentsList
							classroomId={sourceId}
							classroom={selectedClassroom}
							chatBlocked={zoom.chat_blocked}
							onSelect={this.handleSelectedStudent}
							students={this.state.classroomStudents}
							hasUnreadMessages={this.state.chatOverview.find((overview) => overview.classroomId === sourceId)}
						/>
					) : (
						this.renderClassroomList()
					)}
				</div>
			);
		}

		return (
			<MessagePane
				threadKey={selectedThreadKey}
				username={selectedStudentName}
				classroom={this.props.classroom}
				chatBlocked={zoom.chat_blocked}
				avatarColors={this.avatarColors}
				isEmbedded={this.props.isEmbedded}
				handleChatEnable={this.handleToggleSwitch}
			/>
		);
	}

	getChatWindowTopPosition = () => {
		let topValue = 110;

		// required as to offset fixed position due to stickiness of navbar above toolbar
		if (this.props.windowOffset && this.props.windowOffset <= 0) {
			const offsetValue = this.props.windowOffset <= -57 ? -57 : this.props.windowOffset;
			topValue += offsetValue;
		}

		// required as to offset fixed position due to stickiness of announcement bar
		const announcementBar = document.querySelector(".announcement-bar-container");
		if (announcementBar) {
			topValue += announcementBar.offsetHeight;
		}

		return topValue;
	};

	handleChatMinimize = () => {
		if (this.props.handleClose) {
			this.props.handleClose();
			return;
		}
		useStore.getState().chat.toggleChatWindow(true);
	};

	render() {
		const { minimized } = this.state;

		return (
			<Fragment>
				{minimized && (
					<div className="ClassroomChatIcon_minimized" onClick={this.handleChatMinimize}>
						<img src={iconChatMini} alt="Show chat" />
					</div>
				)}
				<div
					style={{ top: `${this.getChatWindowTopPosition()}px` }}
					className={clsx(
						"ClassroomChat_container",
						!this.props.isEmbedded && "ClassroomChat_containerFixed",
						this.props.isEmbedded && "ClassroomChat_embedded",
						minimized && "ClassroomChat_containerMinimised"
					)}
				>
					<div className="ClassroomChat_headerContainer">
						{this.renderHeader()}
						{this.renderRightControl()}
					</div>
					{this.renderContents()}
				</div>
				<ChatEnableModal
					open={this.state.showChatEnableModal}
					onClose={() => {
						this.setState({ showChatEnableModal: false });
						useStore.getState().chat.openChatEnableModal(false);
					}}
					disableChatEnableModal={this.state.disableChatEnableModal}
					handleCheckbox={this.handleEnableModalCheckbox}
					onSubmit={this.handleChatEnableSubmit}
				/>
			</Fragment>
		);
	}
}

ChatWindow.propTypes = {
	classroom: PropTypes.object,
	isChatWorking: PropTypes.bool,
	isEmbedded: PropTypes.bool,
	handleClose: PropTypes.func,
	windowOffset: PropTypes.number,
};

export default ChatWindow;
